import { Badge } from "../../../components/ui/badge";
import { Users, Info, Save, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { BandLogEntryForm } from "../../baende/components/BandLogEntryForm";
import { TagesgastCard } from "./TagesgastCard";

// ============================================================================
// Types
// ============================================================================

export interface FuturePreviewContentProps {
  students: any[];
  isCourse: boolean;
  formatStudentName: (n: string) => string;
  getProtectionLabel: (id: string) => string | null;
  isGreyLabel: (label: string) => boolean;
  lessonNote: string;
  setLessonNote?: (v: string) => void;
  canEditDiary: boolean;
  lessonId?: string;
  /** Audit #25: passed through to TagesgastCard for the auto-move note. */
  lessonName?: string;
  onSaveDiary?: () => Promise<void>;
  isSavingDiary?: boolean;
  courseOverlaps?: Array<{
    courseName: string;
    students: Array<{ id: string; name: string }>;
    courseRoom?: string;
    isCancelled?: boolean;
  }>;
  concurrentCourseAttendance?: any;
  /** Needed to render the band log form in read-only / pre-plan mode */
  bandClassId?: string | null;
  bandLessonDateStr?: string | null;
  bandPeriodNumber?: number | null;
  /** Refresh bundle after a Tagesgast add. */
  reloadBundle?: () => Promise<void>;
}

// ============================================================================
// Component
// ============================================================================

export function FuturePreviewContent({
  students,
  isCourse,
  formatStudentName,
  getProtectionLabel,
  isGreyLabel,
  lessonNote,
  setLessonNote,
  canEditDiary,
  onSaveDiary,
  isSavingDiary = false,
  courseOverlaps,
  concurrentCourseAttendance,
  lessonId,
  lessonName,
  bandClassId,
  bandLessonDateStr,
  bandPeriodNumber,
  reloadBundle,
}: FuturePreviewContentProps) {
  const [diaryEditing, setDiaryEditing] = useState(false);

  const enrolledIds = useMemo(
    () => new Set(students.map((s: any) => String(s.id))),
    [students],
  );

  const handleSaveDiary = async () => {
    if (onSaveDiary) await onSaveDiary();
  };

  return (
    <div className="space-y-4">
      {/* Timing banner */}
      <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
        <p className="text-sm text-blue-800">
          Anwesenheit kann erst 10 Minuten vor Unterrichtsbeginn erfasst werden.
        </p>
      </div>

      {/* Klassenbuch-Eintrag */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-blue-900">Klassenbuch-Eintrag</h4>
          {canEditDiary && !diaryEditing && (
            <button
              onClick={() => setDiaryEditing(true)}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border border-blue-300 text-blue-700 hover:bg-blue-100 transition-colors"
            >
              Bearbeiten
            </button>
          )}
        </div>
        {diaryEditing ? (
          <>
            <textarea
              value={lessonNote}
              onChange={(e) => setLessonNote?.(e.target.value)}
              className="w-full rounded-md border border-blue-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 min-h-[80px] resize-y"
              placeholder="Unterrichtsinhalt eingeben…"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDiaryEditing(false)}
                className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={async () => {
                  await handleSaveDiary();
                  setDiaryEditing(false);
                }}
                disabled={isSavingDiary}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {isSavingDiary ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Save className="h-3.5 w-3.5" />
                )}
                Speichern
              </button>
            </div>
          </>
        ) : lessonNote ? (
          <p className="text-sm text-blue-800 whitespace-pre-wrap">{lessonNote}</p>
        ) : !canEditDiary ? (
          <p className="text-sm text-gray-500 italic">Kein Eintrag vorhanden</p>
        ) : null}
      </div>

      {/* Bänder pre-plan row: show the band that's scheduled for this future
          lesson with Verschoben / Ausgefallen affordances. Durchgeführt is
          suppressed inside the form since the lesson hasn't happened yet. */}
      {lessonId && bandClassId && bandLessonDateStr && (
        <BandLogEntryForm
          lessonId={lessonId}
          classId={bandClassId}
          dateStr={bandLessonDateStr}
          periodNumber={bandPeriodNumber ?? null}
          futureMode
        />
      )}

      {/* Adjacent course overlaps — above student roster */}
      {courseOverlaps && courseOverlaps.length > 0 && (
        <div className="space-y-3">
          {courseOverlaps.filter(c => !c.isCancelled && c.students.length > 0).map((overlap, idx) => (
            <div key={idx} className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <h4 className="text-sm font-medium text-purple-800 mb-2">
                📚 Im Kurs: {overlap.courseName}
                {overlap.courseRoom && <span className="text-purple-500 font-normal"> · {overlap.courseRoom}</span>}
              </h4>
              <div className="grid grid-cols-2 gap-1.5">
                {overlap.students.map((s) => (
                  <div key={s.id} className="flex items-center gap-2 text-sm text-purple-700">
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-400 shrink-0" />
                    {formatStudentName(s.name)}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cancelled concurrent courses from RPC data */}
      {(() => {
        const cancelledCourses = (concurrentCourseAttendance?.courses || []).filter((c: any) => c.is_cancelled);
        if (cancelledCourses.length === 0) return null;
        return (
          <div className="space-y-3">
            {cancelledCourses.map((course: any, idx: number) => (
              <div key={idx} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <h4 className="text-sm font-medium text-red-800 mb-1">
                  ⚠️ <span className="line-through">{course.course_name}</span>
                  <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded-full no-underline">Abgesagt</span>
                  {course.room_name && <span className="ml-2 text-xs font-normal text-gray-500">(Raum {course.room_name})</span>}
                </h4>
                <p className="text-xs text-red-600">
                  {course.students?.length || 0} Schüler*innen zurück in dieser Stunde
                </p>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Tagesgast picker — available before the lockout opens so a teacher
          can pre-stage a guest student. Only meaningful for course lessons. */}
      {isCourse && lessonId && reloadBundle && (
        <TagesgastCard
          lessonId={lessonId}
          lessonName={lessonName}
          enrolledIds={enrolledIds}
          reloadBundle={reloadBundle}
        />
      )}

      {/* Student roster — split into "expected present" vs "expected
          absent" groups so the teacher can scan counts at a glance instead
          of hunting Informiert badges in a mixed list. */}
      {(() => {
        const expectedPresent: any[] = [];
        const expectedAbsent: Array<{ student: any; label: string }> = [];
        for (const student of students) {
          const protLabel = getProtectionLabel(student.id);
          if (protLabel) {
            expectedAbsent.push({ student, label: protLabel });
          } else {
            expectedPresent.push(student);
          }
        }
        return (
          <div className="space-y-4">
            {expectedPresent.length > 0 && (
            <div>
              <h4 className="flex items-center gap-2 mb-3">
                <Users className="h-5 w-5 text-blue-600" /> Voraussichtlich
                anwesend ({expectedPresent.length})
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {expectedPresent.map((student: any) => (
                  <div
                    key={student.id}
                    className="flex items-center gap-2 p-2 rounded-lg overflow-hidden bg-gray-50"
                  >
                    <div className="w-2 h-2 rounded-full shrink-0 bg-blue-400" />
                    <span className="text-sm break-words flex-1 min-w-0">
                      {formatStudentName(student.name)}
                    </span>
                    {isCourse && student.className && (
                      <span className="text-xs text-gray-500 font-medium shrink-0">
                        ({student.className})
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            )}
            {expectedAbsent.length > 0 && (
              <div>
                <h4 className="flex items-center gap-2 mb-3">
                  <Users className="h-5 w-5 text-red-500" /> Voraussichtlich
                  abwesend ({expectedAbsent.length})
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {expectedAbsent.map(({ student, label }) => {
                    const grey = isGreyLabel(label);
                    return (
                      <div
                        key={student.id}
                        className={`flex items-center gap-2 p-2 rounded-lg overflow-hidden ${
                          grey ? "bg-gray-50" : "bg-red-50"
                        }`}
                      >
                        <div
                          className={`w-2 h-2 rounded-full shrink-0 ${
                            grey ? "bg-gray-400" : "bg-red-500"
                          }`}
                        />
                        <span className="text-sm break-words flex-1 min-w-0">
                          {formatStudentName(student.name)}
                        </span>
                        <Badge
                          variant={grey ? "secondary" : "destructive"}
                          className={`text-[10px] px-1.5 py-0 shrink-0 ${
                            grey ? "text-gray-500 bg-gray-200" : ""
                          }`}
                        >
                          {label}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
